import { useState, useMemo } from 'react';
import { ProyectoMock, EvaluacionItem } from '../../../mocks/proyectos';

interface Props {
  proyectos: ProyectoMock[];
  onUpdateProyecto: (codigo: string, updates: Partial<ProyectoMock>) => void;
}

type Decision = 'aprobado' | 'correcciones' | 'no_pertinente' | '';
type ActiveTab = 'evaluaciones' | 'fusion' | 'decision';

const CRITERIOS_LABELS: Record<string, string> = {
  relevancia: 'Relevancia',
  metodologia: 'Metodología',
  viabilidad: 'Viabilidad',
  innovacion: 'Innovación',
  impacto: 'Impacto',
  originalidad: 'Aporte',
  equipo: 'Equipo',
  presupuesto: 'Presupuesto',
};

const decisionConfig = {
  aprobado: {
    label: 'Aprobado científicamente',
    icon: 'ri-checkbox-circle-fill',
    bg: 'bg-emerald-600',
    border: 'border-emerald-500',
    light: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    estadoMap: 'Aprobado',
    dot: 'bg-emerald-500',
  },
  correcciones: {
    label: 'Aprobado con correcciones',
    icon: 'ri-edit-circle-fill',
    bg: 'bg-amber-500',
    border: 'border-amber-400',
    light: 'bg-amber-50 border-amber-200 text-amber-700',
    estadoMap: 'Con correcciones',
    dot: 'bg-amber-400',
  },
  no_pertinente: {
    label: 'No pertinente',
    icon: 'ri-close-circle-fill',
    bg: 'bg-red-500',
    border: 'border-red-400',
    light: 'bg-red-50 border-red-200 text-red-600',
    estadoMap: 'No pertinente',
    dot: 'bg-red-500',
  },
};

function calcPromedioEval(ev: EvaluacionItem): number | null {
  const vals = Object.values(ev.scores).map(v => parseInt(v, 10)).filter(v => !isNaN(v));
  if (vals.length === 0) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function calcPromedioGlobal(evaluaciones: EvaluacionItem[]): number | null {
  const promedios = evaluaciones.map(calcPromedioEval).filter((v): v is number => v !== null);
  if (promedios.length === 0) return null;
  return promedios.reduce((a, b) => a + b, 0) / promedios.length;
}

function getSuggestedDecision(avg: number): Decision {
  if (avg >= 4.0) return 'aprobado';
  if (avg >= 2.5) return 'correcciones';
  return 'no_pertinente';
}

function getScoreColor(avg: number) {
  if (avg >= 4) return 'text-emerald-600';
  if (avg >= 3) return 'text-teal-600';
  if (avg >= 2) return 'text-amber-600';
  return 'text-red-500';
}

function getScoreBg(avg: number) {
  if (avg >= 4) return 'bg-emerald-50 border-emerald-200';
  if (avg >= 3) return 'bg-teal-50 border-teal-200';
  if (avg >= 2) return 'bg-amber-50 border-amber-200';
  return 'bg-red-50 border-red-200';
}

function getScoreLabel(avg: number) {
  if (avg >= 4.5) return 'Excelente';
  if (avg >= 4) return 'Muy bueno';
  if (avg >= 3) return 'Bueno';
  if (avg >= 2) return 'Regular';
  return 'Deficiente';
}

const getEstadoBadge = (estado: string) => {
  if (estado === 'Aprobado') return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
  if (estado === 'Con correcciones') return 'bg-amber-50 text-amber-700 border border-amber-200';
  if (estado === 'No pertinente') return 'bg-red-50 text-red-600 border border-red-200';
  if (estado === 'En evaluación') return 'bg-amber-50 text-amber-700 border border-amber-200';
  return 'bg-slate-50 text-slate-500 border border-slate-200';
};

const getBadge = (d: Decision) => (d && decisionConfig[d]) ? decisionConfig[d].light : '';
const getBadgeLabel = (d: Decision) => (d && decisionConfig[d]) ? decisionConfig[d].label : '';

// Consensus helpers
function calcConsensus(evaluaciones: EvaluacionItem[]) {
  const counts: Record<string, number> = { aprobado: 0, correcciones: 0, no_pertinente: 0, sin_propuesta: 0 };
  for (const ev of evaluaciones) {
    const dp = ev.decisionPropuesta;
    if (dp === 'aprobado' || dp === 'correcciones' || dp === 'no_pertinente') {
      counts[dp]++;
    } else {
      counts.sin_propuesta++;
    }
  }
  return counts;
}

function getMajorityDecision(counts: Record<string, number>): Decision {
  const relevant = [
    { key: 'aprobado' as Decision, val: counts.aprobado },
    { key: 'correcciones' as Decision, val: counts.correcciones },
    { key: 'no_pertinente' as Decision, val: counts.no_pertinente },
  ];
  const max = Math.max(...relevant.map(r => r.val));
  if (max === 0) return '';
  return relevant.find(r => r.val === max)?.key ?? '';
}

// ─── Generador de carta formal ─────────────────────────────────────────────
function buildFormalLetter(
  proyecto: ProyectoMock,
  evaluaciones: EvaluacionItem[],
  justificacion: string,
  finalDecision: Decision,
  requiresEthics?: boolean
): string {
  const today = new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });

  const lines: string[] = [
    today,
    '',
    `Estimado/a ${proyecto.investigador},`,
    '',
    `Por medio de la presente, el Comité Científico se dirige a usted en relación con el proyecto titulado "${proyecto.titulo}" (código: ${proyecto.codigo}), a fin de comunicarle la resolución oficial derivada del proceso de evaluación.`,
    '',
    '═══════════════════════════════════════════════════════════',
    '  1ª RONDA DE EVALUACIÓN — Decisión oficial del Comité',
    '═══════════════════════════════════════════════════════════',
    '',
    'Esta comunicación constituye la PRIMERA resolución oficial emitida por el Comité Científico.',
    '',
    '─── DICTAMEN TÉCNICO DEL COMITÉ ───',
    '',
    justificacion,
    '',
    '─── OBSERVACIONES DE LOS EVALUADORES ───',
    '',
  ];

  const obsEvals = evaluaciones.filter(ev => ev.observaciones);
  if (obsEvals.length > 0) {
    obsEvals.forEach(ev => {
      lines.push(`• ${ev.observaciones}`);
      lines.push('');
    });
  } else {
    lines.push('Los evaluadores no registraron observaciones adicionales.');
    lines.push('');
  }

  lines.push('─── RESOLUCIÓN DEL COMITÉ CIENTÍFICO ───');
  lines.push('');
  lines.push('Ronda: 1ª evaluación oficial');
  lines.push('');

  if (finalDecision === 'aprobado') {
    lines.push(
      'En virtud de lo expuesto, el Comité Científico ha resuelto APROBAR CIENTÍFICAMENTE el proyecto de investigación presentado. Le felicitamos por la calidad del trabajo y le instamos a continuar con las etapas subsiguientes del programa.'
    );
  } else if (finalDecision === 'correcciones') {
    lines.push(
      '1ª RONDA — APROBADO CON CORRECCIONES'
    );
    lines.push('');
    lines.push(
      'En virtud de lo expuesto, el Comité Científico ha resuelto APROBAR CON CORRECCIONES el proyecto en su primera evaluación oficial. Se solicita atender las observaciones detalladas en los apartados anteriores y reenviar el documento corregido dentro del plazo estipulado a través del sistema de seguimiento. Una vez recibido y revisado el documento corregido, el Comité emitirá la retroalimentación correspondiente (2ª ronda) o la resolución definitiva según corresponda.'
    );
  } else {
    lines.push(
      'En virtud de lo expuesto, el Comité Científico ha resuelto declarar el proyecto como NO PERTINENTE en su forma actual, en tanto no satisface los criterios mínimos establecidos para esta convocatoria. Se le invita a revisar los lineamientos del programa y a considerar la reformulación de la propuesta para futuras instancias.'
    );
  }

  if (requiresEthics) {
    lines.push('');
    lines.push('─── DERIVACIÓN AL COMITÉ DE ÉTICA INSTITUCIONAL ───');
    lines.push('');
    lines.push(
      'El Comité Científico ha identificado que el presente proyecto involucra aspectos que requieren revisión formal por parte del Comité de Ética Institucional. Se solicita al equipo investigador gestionar, dentro del plazo estipulado, la presentación formal ante dicho Comité y remitir la resolución correspondiente al Vicerrectorado de Investigación como requisito previo a la continuidad del proyecto.'
    );
  }

  lines.push('');
  lines.push('─── RECOMENDACIONES ADICIONALES ───');
  lines.push('');
  lines.push('[El Administrador puede incorporar aquí recomendaciones adicionales, observaciones específicas o cualquier indicación pertinente antes de enviar esta comunicación al autor.]');
  lines.push('');
  lines.push('Sin más que agregar, el Comité Científico agradece su participación en el programa y queda a su entera disposición para cualquier consulta o aclaración que estime necesaria.');
  lines.push('');
  lines.push('Atentamente,');
  lines.push('');
  lines.push('Comité Científico');
  lines.push('Programa de Investigación');

  return lines.join('\n');
}

// ─── Generador de sugerencia del comité ────────────────────────────────────
function generateCommitteeSuggestion(
  evaluaciones: EvaluacionItem[],
  promedioGlobal: number | null,
  consensus: Record<string, number>,
  majorityDecision: Decision,
  suggestedDecision: Decision | null,
  proyecto: ProyectoMock
): { decision: Decision; justificacion: string; observacionDraft: string; requiresEthics: boolean; ethicsReason: string } | null {
  if (evaluaciones.length === 0 || promedioGlobal === null) return null;

  // Determinar decisión final ponderada (mayoría + puntaje)
  let finalDecision: Decision = '';
  const cntAp = consensus.aprobado || 0;
  const cntCo = consensus.correcciones || 0;
  const cntNP = consensus.no_pertinente || 0;
  const cntSin = consensus.sin_propuesta || 0;

  const majority = majorityDecision;
  const byScore = suggestedDecision;

  if (majority && majority === byScore) {
    finalDecision = majority;
  } else if (majority && promedioGlobal >= 2.0) {
    finalDecision = majority;
  } else if (byScore) {
    finalDecision = byScore;
  } else {
    finalDecision = 'no_pertinente';
  }

  // Detectar necesidad de Comité de Ética
  const ethicsEvals = evaluaciones.filter(
    ev => ev.ethicsFlag && ev.ethicsFlag !== '' && ev.ethicsFlag.toLowerCase() !== 'no' && ev.ethicsFlag.toLowerCase() !== 'no aplica'
  );
  const requiresEthics = ethicsEvals.length > 0;
  const ethicsReason = requiresEthics
    ? `${ethicsEvals.length} evaluador${ethicsEvals.length !== 1 ? 'es han' : ' ha'} indicado que este proyecto involucra aspectos que requieren revisión formal por el Comité de Ética Institucional${ethicsEvals[0]?.ethicsFlag ? ` (${ethicsEvals.map(e => e.ethicsFlag).join(', ')})` : ''}.`
    : '';

  // Calcular promedios por criterio
  const criterioSums: Record<string, number[]> = {};
  for (const ev of evaluaciones) {
    for (const [k, v] of Object.entries(ev.scores)) {
      const n = parseInt(v, 10);
      if (!isNaN(n)) {
        if (!criterioSums[k]) criterioSums[k] = [];
        criterioSums[k].push(n);
      }
    }
  }
  const criterioAvgs = Object.entries(criterioSums).map(([k, vals]) => ({
    key: k,
    label: CRITERIOS_LABELS[k] || k,
    avg: vals.reduce((a, b) => a + b, 0) / vals.length,
  }));
  const fortalezas = criterioAvgs.filter(c => c.avg >= 3.8).map(c => c.label);
  const debilidades = criterioAvgs.filter(c => c.avg < 2.5).map(c => c.label);

  // Construir justificación técnica
  const parts: string[] = [];
  parts.push(`El Comité Científico ha analizado ${evaluaciones.length} evaluación${evaluaciones.length !== 1 ? 'es' : ''} independiente${evaluaciones.length !== 1 ? 's' : ''} para el proyecto "${proyecto.titulo}" (${proyecto.codigo}).`);
  parts.push(`El promedio ponderado de puntajes es ${promedioGlobal.toFixed(2)}/5 (${getScoreLabel(promedioGlobal)}).`);

  if (cntAp + cntCo + cntNP > 0) {
    const breakdown: string[] = [];
    if (cntAp > 0) breakdown.push(`${cntAp} evaluador${cntAp !== 1 ? 'es' : ''} recomienda${cntAp === 1 ? '' : 'n'} aprobación científica`);
    if (cntCo > 0) breakdown.push(`${cntCo} con correcciones`);
    if (cntNP > 0) breakdown.push(`${cntNP} no pertinente`);
    if (cntSin > 0) breakdown.push(`${cntSin} sin propuesta registrada`);
    parts.push(`La distribución de recomendaciones es: ${breakdown.join(', ')}.`);
  }

  if (fortalezas.length > 0) {
    parts.push(`Aspectos destacados positivamente: ${fortalezas.join(', ')}.`);
  }
  if (debilidades.length > 0) {
    parts.push(`Aspectos que requieren atención: ${debilidades.join(', ')}.`);
  }

  if (finalDecision === 'aprobado') {
    parts.push('En virtud de lo anterior, el Comité recomienda la APROBACIÓN CIENTÍFICA del proyecto.');
  } else if (finalDecision === 'correcciones') {
    parts.push('En virtud de lo anterior, el Comité recomienda la APROBACIÓN CON CORRECCIONES. El equipo investigador deberá atender las observaciones indicadas y reenviar el documento corregido dentro del plazo establecido.');
  } else {
    parts.push('En virtud de lo anterior, el Comité recomienda declarar el proyecto NO PERTINENTE en su forma actual, dado que no satisface los criterios mínimos de evaluación establecidos por el programa.');
  }

  const justificacion = parts.join(' ');

  // Construir carta formal completa
  const observacionDraft = buildFormalLetter(proyecto, evaluaciones, justificacion, finalDecision, requiresEthics);

  return { decision: finalDecision, justificacion, observacionDraft, requiresEthics, ethicsReason };
}

export default function ResultadoDecision({ proyectos, onUpdateProyecto }: Props) {
  const [selected, setSelected] = useState<string | null>(null);
  const [decision, setDecision] = useState<Decision>('');
  const [observaciones, setObservaciones] = useState('');
  const [plazo, setPlazo] = useState('');
  const [sugerencia, setSugerencia] = useState('');
  const [saved, setSaved] = useState(false);
  const [emailNotif, setEmailNotif] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>('evaluaciones');
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [suggestionApplied, setSuggestionApplied] = useState(false);

  const proyecto = useMemo(() => proyectos.find(p => p.codigo === selected), [proyectos, selected]);
  const evaluaciones = proyecto?.evaluaciones || [];
  const promedioGlobal = useMemo(() => calcPromedioGlobal(evaluaciones), [evaluaciones]);
  const suggestedDecision = promedioGlobal !== null ? getSuggestedDecision(promedioGlobal) : null;
  const consensus = useMemo(() => calcConsensus(evaluaciones), [evaluaciones]);
  const majorityDecision = useMemo(() => getMajorityDecision(consensus), [consensus]);

  const committeSuggestion = useMemo(() => {
    if (!proyecto) return null;
    return generateCommitteeSuggestion(
      evaluaciones,
      promedioGlobal,
      consensus,
      majorityDecision,
      suggestedDecision,
      proyecto
    );
  }, [proyecto, evaluaciones, promedioGlobal, consensus, majorityDecision, suggestedDecision]);

  const handleSelectProject = (p: ProyectoMock) => {
    setSelected(p.codigo);
    setSaved(false);
    setEmailNotif(null);
    setActiveTab('evaluaciones');
    setShowSuggestion(false);
    setSuggestionApplied(false);
    if (p.decision && p.decision !== '—') {
      if (p.decision.toLowerCase().includes('correc')) setDecision('correcciones');
      else if (p.decision.toLowerCase().includes('no pertinente')) setDecision('no_pertinente');
      else if (p.decision.toLowerCase().includes('aprobado')) setDecision('aprobado');
      else setDecision('');
    } else {
      setDecision('');
    }
    // Pre-cargar carta formal generada por el sistema
    const evals = p.evaluaciones || [];
    const avg = calcPromedioGlobal(evals);
    const cons = calcConsensus(evals);
    const majority = getMajorityDecision(cons);
    const suggested = avg !== null ? getSuggestedDecision(avg) : null;
    const sugg = generateCommitteeSuggestion(evals, avg, cons, majority, suggested, p);
    if (sugg) {
      setObservaciones(sugg.observacionDraft);
    } else {
      // Si no hay evaluaciones, carta base en blanco
      const today = new Date().toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' });
      setObservaciones(
        `${today}\n\nEstimado/a ${p.investigador},\n\nPor medio de la presente, el Comité Científico se dirige a usted en relación con el proyecto titulado "${p.titulo}" (código: ${p.codigo}).\n\n─── RESOLUCIÓN DEL COMITÉ CIENTÍFICO ───\n\n[Ingrese aquí la resolución y recomendaciones del Comité.]\n\nAtentamente,\n\nComité Científico\nPrograma de Investigación`
      );
    }
    setPlazo('');
    setSugerencia('');
  };

  const handleEmitir = () => {
    if (!selected || !decision || !observaciones || !proyecto) return;
    const fechaHoy = new Date().toISOString().split('T')[0];
    const cfg = decisionConfig[decision];
    onUpdateProyecto(selected, {
      decision: cfg.label,
      estado: cfg.estadoMap,
      fechaDecision: fechaHoy,
    });

    // Abrir cliente de correo con la carta completa pre-cargada
    const subject = encodeURIComponent(
      `Resolución Oficial del Comité Científico — Proyecto ${proyecto.codigo}`
    );
    const body = encodeURIComponent(observaciones);
    const mailtoLink = `mailto:${proyecto.email}?subject=${subject}&body=${body}`;
    window.open(mailtoLink, '_blank');

    setEmailNotif(proyecto.email);
    setSaved(true);
    setTimeout(() => {
      setSaved(false);
      setEmailNotif(null);
    }, 12000);
  };

  const handleApplySuggestion = () => {
    if (!committeSuggestion) return;
    setDecision(committeSuggestion.decision);
    setObservaciones(committeSuggestion.observacionDraft);
    setSuggestionApplied(true);
    setShowSuggestion(true);
  };

  const proyectosConDecision = useMemo(
    () => proyectos.filter(p => p.decision && p.decision !== '—'),
    [proyectos]
  );

  const criterioKeys = Object.keys(CRITERIOS_LABELS);

  const TABS: { key: ActiveTab; label: string; icon: string; count?: number }[] = [
    { key: 'evaluaciones', label: `Evaluaciones (${evaluaciones.length})`, icon: 'ri-team-line' },
    { key: 'fusion', label: 'Fusión de decisiones', icon: 'ri-git-merge-line' },
    { key: 'decision', label: 'Decisión oficial', icon: 'ri-shield-star-line' },
  ];

  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-8 h-8 flex items-center justify-center bg-teal-600 rounded-md">
            <i className="ri-award-line text-white text-lg" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800">Resultado y Decisión</h2>
        </div>
        <p className="text-slate-500 text-sm ml-11">
          Visualice las evaluaciones recibidas, consolide las decisiones propuestas y emita la resolución oficial del Comité Científico.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Lista de todos los proyectos */}
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-3">
            Todos los proyectos ({proyectos.length})
          </h3>
          {proyectos.length === 0 ? (
            <div className="bg-slate-50 border border-dashed border-slate-200 rounded-lg p-8 text-center text-slate-400 text-sm">
              No hay proyectos registrados todavía.
            </div>
          ) : (
            <div className="space-y-2 max-h-[580px] overflow-y-auto pr-1">
              {proyectos.map(p => {
                const evals = p.evaluaciones || [];
                const avg = calcPromedioGlobal(evals);
                const decKey: Decision = p.decision && p.decision !== '—'
                  ? (p.decision.toLowerCase().includes('correc') ? 'correcciones'
                    : p.decision.toLowerCase().includes('no pertinente') ? 'no_pertinente'
                    : 'aprobado')
                  : '';
                const cnt = calcConsensus(evals);
                const hasProposals = (cnt.aprobado + cnt.correcciones + cnt.no_pertinente) > 0;

                return (
                  <button
                    key={p.codigo}
                    onClick={() => handleSelectProject(p)}
                    className={`cursor-pointer w-full text-left p-3.5 rounded-lg border transition-all ${
                      selected === p.codigo
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-slate-200 bg-white hover:border-teal-300'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2 mb-1.5">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-teal-600">{p.codigo}</p>
                        <p className="text-sm font-medium text-slate-800 truncate">{p.titulo}</p>
                        <p className="text-xs text-slate-500">{p.investigador} · {p.area}</p>
                      </div>
                      <span className={`whitespace-nowrap text-xs px-2 py-0.5 rounded-full border font-medium ${getEstadoBadge(p.estado)}`}>
                        {p.estado}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-xs flex items-center gap-1 ${evals.length > 0 ? 'text-teal-600 bg-teal-50 border border-teal-100' : 'text-slate-400 bg-slate-50 border border-slate-100'} px-2 py-0.5 rounded-full`}>
                        <span className="w-3 h-3 flex items-center justify-center"><i className="ri-team-line" /></span>
                        {evals.length} eval.
                      </span>
                      {avg !== null && (
                        <span className={`text-xs px-2 py-0.5 rounded-full border font-semibold ${getScoreBg(avg)}`}>
                          Prom. {avg.toFixed(2)}/5
                        </span>
                      )}
                      {hasProposals && (
                        <span className="text-xs px-2 py-0.5 rounded-full border font-medium bg-violet-50 border-violet-200 text-violet-700 flex items-center gap-1">
                          <span className="w-3 h-3 flex items-center justify-center"><i className="ri-git-merge-line" /></span>
                          {cnt.aprobado + cnt.correcciones + cnt.no_pertinente} propuesta{(cnt.aprobado + cnt.correcciones + cnt.no_pertinente) !== 1 ? 's' : ''}
                        </span>
                      )}
                      {decKey && (
                        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${getBadge(decKey)}`}>
                          {getBadgeLabel(decKey)}
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Panel derecho */}
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          {proyecto ? (
            <div>
              {/* Cabecera proyecto */}
              <div className="bg-slate-50 border-b border-slate-200 px-5 py-4">
                <p className="text-xs font-semibold text-teal-600 mb-0.5">{proyecto.codigo} · {proyecto.area}</p>
                <p className="text-sm font-semibold text-slate-800">{proyecto.titulo}</p>
                <p className="text-xs text-slate-500 mt-0.5">{proyecto.investigador}</p>
                <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1.5">
                  <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-mail-line" /></span>
                  {proyecto.email}
                </p>
              </div>

              {/* Tabs */}
              <div className="flex border-b border-slate-200 bg-white px-1 py-1 gap-1">
                {TABS.map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`whitespace-nowrap flex-1 py-2 text-xs font-semibold rounded-md transition-all cursor-pointer ${
                      activeTab === tab.key
                        ? tab.key === 'decision'
                          ? 'bg-teal-700 text-white'
                          : 'bg-teal-600 text-white'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <span className="flex items-center justify-center gap-1.5">
                      <span className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className={tab.icon} />
                      </span>
                      {tab.label}
                    </span>
                  </button>
                ))}
              </div>

              <div className="p-5 overflow-y-auto max-h-[520px]">
                {/* ══ TAB: EVALUACIONES ══ */}
                {activeTab === 'evaluaciones' && (
                  <div>
                    {evaluaciones.length === 0 ? (
                      <div className="text-center py-8 text-slate-400">
                        <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                          <i className="ri-team-line text-3xl" />
                        </div>
                        <p className="text-sm">Aún no hay evaluaciones registradas para este proyecto.</p>
                        <p className="text-xs mt-1">Los evaluadores deben ingresar en la sección de "Evaluación".</p>
                      </div>
                    ) : (
                      <>
                        {/* Promedio global */}
                        {promedioGlobal !== null && (
                          <div className={`flex items-center gap-4 border rounded-lg px-4 py-3 mb-4 ${getScoreBg(promedioGlobal)}`}>
                            <div>
                              <p className="text-xs text-slate-500">Promedio global</p>
                              <p className={`text-2xl font-extrabold ${getScoreColor(promedioGlobal)}`}>
                                {promedioGlobal.toFixed(2)}
                                <span className="text-sm font-normal text-slate-400"> / 5</span>
                              </p>
                            </div>
                            <div className="flex-1">
                              <p className={`text-sm font-semibold ${getScoreColor(promedioGlobal)}`}>
                                {getScoreLabel(promedioGlobal)}
                              </p>
                              <p className="text-xs text-slate-500">{evaluaciones.length} evaluador{evaluaciones.length !== 1 ? 'es' : ''}</p>
                            </div>
                            {suggestedDecision && (
                              <div className="flex-shrink-0">
                                <p className="text-xs text-slate-400 mb-1">Sugerida por puntaje</p>
                                <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${getBadge(suggestedDecision)}`}>
                                  {getBadgeLabel(suggestedDecision)}
                                </span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Tabla de evaluadores */}
                        <div className="overflow-x-auto rounded-lg border border-slate-200 mb-4">
                          <table className="w-full text-xs min-w-[520px]">
                            <thead className="bg-slate-50 border-b border-slate-200">
                              <tr>
                                <th className="text-left px-3 py-2.5 font-semibold text-slate-500 whitespace-nowrap">Evaluador</th>
                                {criterioKeys.map(k => (
                                  <th key={k} className="text-center px-2 py-2.5 font-semibold text-slate-400 whitespace-nowrap text-[10px]">
                                    {CRITERIOS_LABELS[k]}
                                  </th>
                                ))}
                                <th className="text-center px-3 py-2.5 font-semibold text-slate-500 whitespace-nowrap">Prom.</th>
                                <th className="text-left px-3 py-2.5 font-semibold text-slate-500 whitespace-nowrap">Propuesta</th>
                                <th className="text-left px-3 py-2.5 font-semibold text-slate-500 whitespace-nowrap">Fecha</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {evaluaciones.map((ev, i) => {
                                const avg = calcPromedioEval(ev);
                                const dp = ev.decisionPropuesta;
                                const dpCfg = dp && decisionConfig[dp] ? decisionConfig[dp] : null;
                                return (
                                  <tr key={i} className="bg-white hover:bg-slate-50">
                                    <td className="px-3 py-2.5 font-medium text-slate-700 whitespace-nowrap">{ev.evaluador}</td>
                                    {criterioKeys.map(k => {
                                      const val = parseInt(ev.scores[k] || '', 10);
                                      return (
                                        <td key={k} className="px-2 py-2.5 text-center">
                                          {isNaN(val) ? (
                                            <span className="text-slate-300">—</span>
                                          ) : (
                                            <span className={`font-semibold ${val >= 4 ? 'text-emerald-600' : val >= 3 ? 'text-teal-600' : val >= 2 ? 'text-amber-600' : 'text-red-500'}`}>
                                              {val}
                                            </span>
                                          )}
                                        </td>
                                      );
                                    })}
                                    <td className="px-3 py-2.5 text-center">
                                      {avg !== null ? (
                                        <span className={`font-bold ${getScoreColor(avg)}`}>{avg.toFixed(1)}</span>
                                      ) : <span className="text-slate-300">—</span>}
                                    </td>
                                    <td className="px-3 py-2.5">
                                      {dpCfg ? (
                                        <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${dpCfg.light}`}>
                                          {dpCfg.label}
                                        </span>
                                      ) : (
                                        <span className="text-slate-300 text-xs">Sin propuesta</span>
                                      )}
                                    </td>
                                    <td className="px-3 py-2.5 text-slate-400 whitespace-nowrap">{ev.fecha}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>

                        {/* Observaciones */}
                        {evaluaciones.some(ev => ev.observaciones) && (
                          <div className="space-y-2">
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Observaciones individuales</p>
                            {evaluaciones.filter(ev => ev.observaciones).map((ev, i) => (
                              <div key={i} className="bg-slate-50 border border-slate-100 rounded-md p-3">
                                <p className="text-xs font-semibold text-teal-700 mb-1">{ev.evaluador}</p>
                                <p className="text-xs text-slate-600 leading-relaxed">{ev.observaciones}</p>
                              </div>
                            ))}
                          </div>
                        )}

                        <button
                          onClick={() => setActiveTab('fusion')}
                          className="whitespace-nowrap mt-4 w-full flex items-center justify-center gap-2 bg-teal-600 text-white py-2.5 rounded-md text-sm font-semibold hover:bg-teal-700 transition-colors cursor-pointer"
                        >
                          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-git-merge-line" /></span>
                          Ver fusión de decisiones
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* ══ TAB: FUSIÓN DE DECISIONES ══ */}
                {activeTab === 'fusion' && (
                  <div className="space-y-5">
                    {/* Banner de info */}
                    <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 flex items-start gap-3">
                      <span className="w-5 h-5 flex items-center justify-center text-violet-600 flex-shrink-0 mt-0.5">
                        <i className="ri-git-merge-line" />
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-violet-800 mb-0.5">Consolidación de decisiones</p>
                        <p className="text-xs text-violet-600 leading-relaxed">
                          A continuación se centralizan todas las decisiones y observaciones propuestas por los evaluadores.
                          Esta información es exclusiva del Administrador Principal para facilitar la emisión de la resolución oficial.
                        </p>
                      </div>
                    </div>

                    {evaluaciones.length === 0 ? (
                      <div className="text-center py-8 text-slate-400">
                        <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                          <i className="ri-git-merge-line text-3xl" />
                        </div>
                        <p className="text-sm">No hay evaluaciones ni propuestas aún.</p>
                      </div>
                    ) : (
                      <>
                        {/* Resumen de consenso */}
                        <div>
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                            Consenso de los evaluadores
                          </p>
                          <div className="grid grid-cols-3 gap-2">
                            {(Object.entries(decisionConfig) as [Decision, typeof decisionConfig[keyof typeof decisionConfig]][]).map(([key, cfg]) => {
                              const count = consensus[key as string] || 0;
                              const total = evaluaciones.length;
                              const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                              const isMajority = key === majorityDecision && count > 0;
                              return (
                                <div
                                  key={key}
                                  className={`rounded-lg border p-3 transition-all ${isMajority ? `${cfg.light} ring-2 ring-offset-1 ${cfg.border}` : 'bg-slate-50 border-slate-200'}`}
                                >
                                  <div className="flex items-center gap-1.5 mb-2">
                                    <span className={`w-3.5 h-3.5 flex items-center justify-center ${isMajority ? '' : 'text-slate-400'}`}>
                                      <i className={cfg.icon} />
                                    </span>
                                    {isMajority && (
                                      <span className="text-[10px] font-bold uppercase tracking-wide">Mayoría</span>
                                    )}
                                  </div>
                                  <p className={`text-2xl font-extrabold mb-0.5 ${isMajority ? '' : 'text-slate-600'}`}>{count}</p>
                                  <p className="text-[10px] font-medium leading-tight text-slate-500">{cfg.label}</p>
                                  {total > 0 && (
                                    <div className="mt-2 h-1 rounded-full bg-slate-200 overflow-hidden">
                                      <div
                                        className={`h-full rounded-full ${cfg.bg}`}
                                        style={{ width: `${pct}%` }}
                                      />
                                    </div>
                                  )}
                                  <p className="text-[10px] text-slate-400 mt-1">{pct}%</p>
                                </div>
                              );
                            })}
                          </div>
                          {consensus.sin_propuesta > 0 && (
                            <p className="mt-2 text-xs text-slate-400 flex items-center gap-1.5">
                              <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-information-line" /></span>
                              {consensus.sin_propuesta} evaluador{consensus.sin_propuesta !== 1 ? 'es' : ''} no registró propuesta de decisión.
                            </p>
                          )}
                        </div>

                        {/* Documento consolidado */}
                        <div>
                          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                            <span className="w-4 h-4 flex items-center justify-center text-teal-600"><i className="ri-file-list-3-line" /></span>
                            Documento consolidado de observaciones
                          </p>
                          <div className="space-y-3 border border-slate-200 rounded-lg overflow-hidden divide-y divide-slate-100">
                            {evaluaciones.map((ev, i) => {
                              const avg = calcPromedioEval(ev);
                              const dp = ev.decisionPropuesta;
                              const dpCfg = dp && decisionConfig[dp] ? decisionConfig[dp] : null;
                              return (
                                <div key={i} className="p-4 bg-white">
                                  {/* Evaluador header */}
                                  <div className="flex items-start justify-between gap-3 mb-3">
                                    <div>
                                      <p className="text-sm font-semibold text-slate-800">{ev.evaluador}</p>
                                      <p className="text-xs text-slate-400">{ev.fecha}</p>
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap justify-end">
                                      {avg !== null && (
                                        <span className={`text-xs px-2 py-0.5 rounded-full border font-bold ${getScoreBg(avg)} ${getScoreColor(avg)}`}>
                                          {avg.toFixed(1)}/5
                                        </span>
                                      )}
                                      {dpCfg ? (
                                        <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${dpCfg.light}`}>
                                          <span className="flex items-center gap-1">
                                            <i className={dpCfg.icon} />
                                            {dpCfg.label}
                                          </span>
                                        </span>
                                      ) : (
                                        <span className="text-xs px-2 py-0.5 rounded-full border bg-slate-50 text-slate-400 border-slate-200">
                                          Sin propuesta
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {/* Puntajes por criterio */}
                                  <div className="grid grid-cols-4 gap-1.5 mb-3">
                                    {criterioKeys.map(k => {
                                      const val = parseInt(ev.scores[k] || '', 10);
                                      return (
                                        <div key={k} className="bg-slate-50 rounded p-1.5 text-center border border-slate-100">
                                          <p className="text-[10px] text-slate-400 leading-tight mb-0.5">{CRITERIOS_LABELS[k]}</p>
                                          {isNaN(val) ? (
                                            <p className="text-xs text-slate-300">—</p>
                                          ) : (
                                            <p className={`text-sm font-bold ${val >= 4 ? 'text-emerald-600' : val >= 3 ? 'text-teal-600' : val >= 2 ? 'text-amber-600' : 'text-red-500'}`}>
                                              {val}
                                            </p>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                  {/* Recomendación + ética */}
                                  <div className="flex flex-wrap gap-2 mb-2">
                                    {ev.recomendacion && (
                                      <span className="text-xs bg-slate-50 border border-slate-200 text-slate-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                                        <i className="ri-award-line" />
                                        {ev.recomendacion}
                                      </span>
                                    )}
                                    {ev.ethicsFlag && (
                                      <span className="text-xs bg-slate-50 border border-slate-200 text-slate-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                                        <i className="ri-shield-check-line" />
                                        Ética: {ev.ethicsFlag}
                                      </span>
                                    )}
                                  </div>
                                  {/* Observaciones */}
                                  {ev.observaciones ? (
                                    <div className="bg-slate-50 border border-slate-100 rounded-md p-3">
                                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1">Observaciones</p>
                                      <p className="text-xs text-slate-600 leading-relaxed">{ev.observaciones}</p>
                                    </div>
                                  ) : (
                                    <p className="text-xs text-slate-300 italic">Sin observaciones registradas.</p>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Sugerencia de decisión consolidada */}
                        {majorityDecision && (
                          <div className={`rounded-lg border p-4 ${decisionConfig[majorityDecision].light}`}>
                            <p className="text-xs font-semibold uppercase tracking-wide mb-1.5 opacity-70">Decisión sugerida por mayoría</p>
                            <div className="flex items-center gap-2">
                              <span className="w-5 h-5 flex items-center justify-center">
                                <i className={`${decisionConfig[majorityDecision].icon} text-base`} />
                              </span>
                              <p className="text-sm font-bold">{decisionConfig[majorityDecision].label}</p>
                              <span className="ml-auto text-xs opacity-70">{consensus[majorityDecision]} de {evaluaciones.length} evaluadores</span>
                            </div>
                          </div>
                        )}

                        <button
                          onClick={() => setActiveTab('decision')}
                          className="whitespace-nowrap w-full flex items-center justify-center gap-2 bg-teal-700 text-white py-2.5 rounded-md text-sm font-semibold hover:bg-teal-800 transition-colors cursor-pointer"
                        >
                          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-shield-star-line" /></span>
                          Emitir decisión oficial
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* ══ TAB: DECISIÓN OFICIAL ══ */}
                {activeTab === 'decision' && (
                  <div className="space-y-4">
                    {/* Banner 1ª Ronda — prominente */}
                    <div className="bg-amber-500 rounded-lg p-4 flex items-start gap-3">
                      <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg flex-shrink-0">
                        <i className="ri-number-1 text-white text-xl font-bold" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-bold text-white mb-0.5">1ª RONDA DE EVALUACIÓN — Decisión oficial al autor</p>
                        <p className="text-xs text-amber-100 leading-relaxed">
                          Al emitir esta decisión, el Comité Científico entrega la <strong>primera resolución oficial</strong> al investigador.
                          Si la decisión es "Aprobado con correcciones", el autor deberá atender las observaciones y reenviar el documento corregido.
                          Las revisiones posteriores se gestionan en <strong>Seguimiento y Reportes</strong> (2ª y 3ª ronda).
                        </p>
                      </div>
                      <span className="whitespace-nowrap text-[10px] font-bold uppercase tracking-wide bg-white/20 text-white px-2.5 py-1 rounded-full flex-shrink-0">
                        Ronda 1/3
                      </span>
                    </div>

                    {/* Banner admin-only */}
                    <div className="bg-teal-700 rounded-lg p-4 flex items-start gap-3">
                      <span className="w-5 h-5 flex items-center justify-center text-teal-200 flex-shrink-0 mt-0.5">
                        <i className="ri-shield-star-line" />
                      </span>
                      <div>
                        <p className="text-sm font-bold text-white mb-0.5">Acción exclusiva — Administrador Principal</p>
                        <p className="text-xs text-teal-200 leading-relaxed">
                          Solo el Administrador Principal puede emitir y enviar la decisión oficial al autor.
                          Los demás evaluadores completan su proceso sin acceso a este envío.
                        </p>
                      </div>
                    </div>

                    {/* ── SUGERENCIA DEL SISTEMA ── */}
                    {committeSuggestion && (
                      <div className="rounded-lg border border-teal-200 overflow-hidden">
                        <button
                          onClick={() => setShowSuggestion(prev => !prev)}
                          className="cursor-pointer w-full flex items-center justify-between gap-3 px-4 py-3 bg-teal-50 hover:bg-teal-100 transition-colors"
                        >
                          <div className="flex items-center gap-2.5">
                            <span className="w-6 h-6 flex items-center justify-center bg-teal-600 rounded-md text-white">
                              <i className="ri-robot-2-line text-sm" />
                            </span>
                            <div className="text-left">
                              <p className="text-sm font-bold text-teal-800">Sugerencia del sistema</p>
                              <p className="text-xs text-teal-600">
                                Análisis automático basado en puntajes, propuestas y observaciones de los evaluadores
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {committeSuggestion.decision && (
                              <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${getBadge(committeSuggestion.decision)}`}>
                                {getBadgeLabel(committeSuggestion.decision)}
                              </span>
                            )}
                            <span className="w-5 h-5 flex items-center justify-center text-teal-600">
                              <i className={showSuggestion ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} />
                            </span>
                          </div>
                        </button>

                        {showSuggestion && (
                          <div className="px-4 pb-4 pt-3 bg-white space-y-3">
                            {/* Decisión sugerida */}
                            <div className={`flex items-center gap-3 rounded-md border p-3 ${getBadge(committeSuggestion.decision)}`}>
                              <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                                <i className={`${decisionConfig[committeSuggestion.decision]?.icon} text-base`} />
                              </span>
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-wide opacity-70 mb-0.5">Decisión recomendada</p>
                                <p className="text-sm font-bold">{getBadgeLabel(committeSuggestion.decision)}</p>
                              </div>
                            </div>

                            {/* Justificación */}
                            <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
                              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-quill-pen-line" /></span>
                                Justificación técnica
                              </p>
                              <p className="text-xs text-slate-600 leading-relaxed">{committeSuggestion.justificacion}</p>
                            </div>

                            {/* Vista previa del borrador */}
                            <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
                              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide mb-1.5 flex items-center gap-1">
                                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-file-text-line" /></span>
                                Borrador de comunicación al autor
                              </p>
                              <p className="text-xs text-slate-600 leading-relaxed whitespace-pre-line">{committeSuggestion.observacionDraft}</p>
                            </div>

                            {/* Botón aplicar */}
                            {suggestionApplied ? (
                              <div className="flex items-center gap-2 text-xs text-teal-700 bg-teal-50 border border-teal-200 rounded-md px-3 py-2">
                                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-checkbox-circle-fill" /></span>
                                Sugerencia aplicada — puede seguir editando los campos abajo
                              </div>
                            ) : (
                              <button
                                onClick={handleApplySuggestion}
                                className="cursor-pointer w-full flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md py-2.5 text-sm font-semibold transition-colors"
                              >
                                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-magic-line" /></span>
                                Aplicar sugerencia del sistema
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Resumen de evaluaciones + promedio */}
                    {promedioGlobal !== null && (
                      <div className={`flex items-center gap-3 border rounded-lg px-4 py-3 ${getScoreBg(promedioGlobal)}`}>
                        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                          <i className="ri-bar-chart-line text-slate-500" />
                        </div>
                        <div className="flex-1">
                          <p className="text-xs text-slate-500">Promedio de {evaluaciones.length} evaluación{evaluaciones.length !== 1 ? 'es' : ''}</p>
                          <p className={`text-lg font-extrabold ${getScoreColor(promedioGlobal)}`}>
                            {promedioGlobal.toFixed(2)} / 5 — {getScoreLabel(promedioGlobal)}
                          </p>
                        </div>
                        {majorityDecision && (
                          <div className="flex-shrink-0 text-right">
                            <p className="text-xs text-slate-400 mb-1">Mayoría propone</p>
                            <span className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${getBadge(majorityDecision)}`}>
                              {getBadgeLabel(majorityDecision)}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Conteo de recomendaciones por tipo */}
                    {evaluaciones.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-pie-chart-line text-teal-600" /></span>
                          Recomendaciones de los evaluadores
                        </p>
                        <div className="grid grid-cols-3 gap-2">
                          {(Object.entries(decisionConfig) as [Decision, typeof decisionConfig[keyof typeof decisionConfig]][]).map(([key, cfg]) => {
                            const count = consensus[key as string] || 0;
                            const total = evaluaciones.length;
                            const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                            const isMajority = key === majorityDecision && count > 0;
                            return (
                              <div
                                key={key}
                                className={`rounded-lg border p-3 transition-all ${isMajority ? `${cfg.light} ring-2 ring-offset-1 ${cfg.border}` : 'bg-slate-50 border-slate-200'}`}
                              >
                                <div className="flex items-center justify-between mb-1.5">
                                  <span className={`w-5 h-5 flex items-center justify-center ${isMajority ? '' : 'text-slate-400'}`}>
                                    <i className={`${cfg.icon} text-base`} />
                                  </span>
                                  {isMajority && (
                                    <span className="text-[10px] font-bold uppercase tracking-wide bg-white/60 px-1.5 py-0.5 rounded-full">
                                      Mayoría
                                    </span>
                                  )}
                                </div>
                                <p className={`text-2xl font-extrabold ${isMajority ? '' : 'text-slate-600'}`}>{count}</p>
                                <p className="text-[10px] font-medium leading-tight text-slate-500 mt-0.5">{cfg.label}</p>
                                {total > 0 && (
                                  <>
                                    <div className="mt-2 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                                      <div className={`h-full rounded-full ${cfg.bg}`} style={{ width: `${pct}%` }} />
                                    </div>
                                    <p className="text-[10px] text-slate-400 mt-1">{pct}% · {count} de {total}</p>
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {consensus.sin_propuesta > 0 && (
                          <p className="mt-1.5 text-xs text-slate-400 flex items-center gap-1.5">
                            <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-information-line" /></span>
                            {consensus.sin_propuesta} evaluador{consensus.sin_propuesta !== 1 ? 'es' : ''} sin recomendación registrada.
                          </p>
                        )}
                      </div>
                    )}

                    {evaluaciones.length === 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-700 flex items-start gap-2">
                        <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-information-line" /></span>
                        Este proyecto aún no tiene evaluaciones. Puede emitir una decisión de todas formas.
                      </div>
                    )}

                    {/* Decisión */}
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">
                        Decisión final del Comité Científico <span className="text-red-500">*</span>
                      </label>
                      <div className="space-y-2">
                        {(Object.entries(decisionConfig) as [Decision, typeof decisionConfig[keyof typeof decisionConfig]][]).map(([key, cfg]) => (
                          <button
                            key={key}
                            onClick={() => setDecision(key as Decision)}
                            className={`cursor-pointer w-full flex items-center gap-3 p-3 rounded-md border text-sm font-medium transition-all ${
                              decision === key
                                ? `${cfg.bg} text-white border-transparent`
                                : 'border-slate-200 text-slate-700 hover:border-slate-300 bg-white'
                            }`}
                          >
                            <span className="w-5 h-5 flex items-center justify-center text-base flex-shrink-0">
                              <i className={cfg.icon} />
                            </span>
                            <span className="flex-1 text-left">{cfg.label}</span>
                            {committeSuggestion?.decision === key && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${decision === key ? 'bg-white/20 text-white' : 'bg-teal-50 text-teal-700 border border-teal-200'}`}>
                                Sugerida
                              </span>
                            )}
                            {majorityDecision === key && majorityDecision !== committeSuggestion?.decision && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${decision === key ? 'bg-white/20 text-white' : 'bg-violet-50 text-violet-700 border border-violet-200'}`}>
                                Mayoría ({consensus[key as string] || 0})
                              </span>
                            )}
                            {suggestedDecision === key && suggestedDecision !== majorityDecision && suggestedDecision !== committeSuggestion?.decision && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${decision === key ? 'bg-white/20 text-white' : 'bg-teal-50 text-teal-700 border border-teal-200'}`}>
                                Por puntaje
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* ── RECOMENDACIÓN COMITÉ DE ÉTICA ── */}
                    {committeSuggestion?.requiresEthics && (
                      <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
                        <div className="flex items-start gap-3">
                          <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-md flex-shrink-0">
                            <i className="ri-shield-check-line text-amber-600 text-lg" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-bold text-amber-800 mb-1 flex items-center gap-2">
                              Derivación al Comité de Ética recomendada
                              <span className="text-xs font-medium bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full">Requerido</span>
                            </p>
                            <p className="text-xs text-amber-700 leading-relaxed mb-2">
                              {committeSuggestion.ethicsReason}
                            </p>
                            <p className="text-xs text-amber-600 flex items-center gap-1.5">
                              <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-file-text-line" /></span>
                              Esta recomendación fue incluida automáticamente en la carta de comunicación al autor.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {decision === 'correcciones' && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">
                          Plazo para correcciones <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          value={plazo}
                          onChange={e => setPlazo(e.target.value)}
                          className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50 cursor-pointer"
                        />
                      </div>
                    )}

                    {decision === 'no_pertinente' && (
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Sugerencia de reencauzamiento</label>
                        <input
                          type="text"
                          value={sugerencia}
                          onChange={e => setSugerencia(e.target.value)}
                          placeholder="Ej.: Fusionar con PRY-2026-001 o reorientar hacia línea X"
                          className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50"
                        />
                      </div>
                    )}

                    {/* Observaciones consolidadas */}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <label className="block text-sm font-medium text-slate-700">
                          Carta de comunicación al autor <span className="text-red-500">*</span>
                        </label>
                        <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">
                          1ª Ronda
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 mb-1">
                        ({observaciones.length}/3000 caracteres)
                      </p>
                      <div className="flex items-start gap-2 bg-teal-50 border border-teal-100 rounded-md px-3 py-2 mb-2">
                        <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 text-teal-600 mt-0.5">
                          <i className="ri-file-text-line" />
                        </span>
                        <p className="text-xs text-teal-700 leading-relaxed">
                          {suggestionApplied
                            ? 'La carta fue generada por el sistema e incluye la justificación técnica y las observaciones. Puede ampliarla antes de enviarla.'
                            : 'Esta carta formal fue pre-generada automáticamente con la justificación técnica del sistema y las observaciones de los evaluadores. Puede añadir más recomendaciones antes de enviarla al autor.'}
                        </p>
                      </div>
                      <textarea
                        value={observaciones}
                        onChange={e => e.target.value.length <= 3000 && setObservaciones(e.target.value)}
                        rows={12}
                        placeholder="La carta formal aparecerá aquí automáticamente al seleccionar el proyecto..."
                        className="w-full border border-slate-200 rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-teal-500 bg-slate-50 resize-none font-mono leading-relaxed"
                      />
                    </div>

                    {!decision && (
                      <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-xs text-amber-700 flex items-start gap-2">
                        <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5"><i className="ri-information-line" /></span>
                        Seleccione una decisión y complete las observaciones para habilitar el envío.
                      </div>
                    )}

                    {emailNotif && saved && (
                      <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 space-y-3">
                        <p className="text-sm text-teal-700 font-bold flex items-center gap-2">
                          <span className="w-5 h-5 flex items-center justify-center bg-teal-600 rounded-full text-white">
                            <i className="ri-checkbox-circle-fill text-xs" />
                          </span>
                          Decisión registrada — correo abierto automáticamente
                        </p>
                        <p className="text-xs text-teal-700 pl-7 leading-relaxed">
                          Se abrió su cliente de correo con la carta completa dirigida a{' '}
                          <strong className="font-semibold">{emailNotif}</strong>.
                          Solo debe hacer clic en <strong>"Enviar"</strong> para que el autor reciba la comunicación oficial.
                        </p>
                        <div className="pl-7">
                          <button
                            onClick={() => {
                              if (!proyecto) return;
                              const subject = encodeURIComponent(
                                `Resolución Oficial del Comité Científico — Proyecto ${proyecto.codigo}`
                              );
                              const body = encodeURIComponent(observaciones);
                              window.open(`mailto:${proyecto.email}?subject=${subject}&body=${body}`, '_blank');
                            }}
                            className="whitespace-nowrap inline-flex items-center gap-2 text-xs bg-teal-600 hover:bg-teal-700 text-white px-3 py-2 rounded-md font-semibold transition-colors cursor-pointer"
                          >
                            <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-mail-send-line" /></span>
                            Reabrir correo si se cerró accidentalmente
                          </button>
                        </div>
                      </div>
                    )}

                    <button
                      onClick={handleEmitir}
                      disabled={!decision || !observaciones}
                      className="whitespace-nowrap w-full bg-teal-700 text-white py-3 rounded-md text-sm font-bold hover:bg-teal-800 transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <span className="w-4 h-4 flex items-center justify-center"><i className="ri-send-plane-fill" /></span>
                      Emitir y enviar decisión oficial al autor
                    </button>
                    <p className="text-xs text-center text-slate-400 flex items-center justify-center gap-1.5">
                      <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-lock-line" /></span>
                      Esta acción solo está habilitada para el Administrador Principal
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-16 text-slate-400">
              <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2">
                <i className="ri-award-line text-3xl" />
              </div>
              <p className="text-sm">Seleccione un proyecto para ver sus evaluaciones y emitir la decisión</p>
            </div>
          )}
        </div>
      </div>

      {/* Historial de decisiones emitidas */}
      {proyectosConDecision.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-history-line" /></span>
            Decisiones oficiales emitidas — 1ª Ronda ({proyectosConDecision.length})
          </h3>
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {['Código', 'Título', 'Investigador', 'Evaluadores', 'Prom. global', 'Decisión oficial', 'Ronda', 'Notificado a', 'Fecha'].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {proyectosConDecision.map(p => {
                  const evals = p.evaluaciones || [];
                  const avg = calcPromedioGlobal(evals);
                  const dec: Decision = p.decision.toLowerCase().includes('correc') ? 'correcciones'
                    : p.decision.toLowerCase().includes('no pertinente') ? 'no_pertinente'
                    : 'aprobado';
                  return (
                    <tr key={p.codigo} className="bg-white hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono text-xs font-semibold text-teal-700">{p.codigo}</td>
                      <td className="px-4 py-3 text-slate-700 max-w-[180px]">
                        <span className="block truncate text-xs" title={p.titulo}>{p.titulo}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">{p.investigador}</td>
                      <td className="px-4 py-3 text-xs">
                        {evals.length > 0 ? (
                          <span className="text-teal-700 bg-teal-50 border border-teal-100 px-2 py-0.5 rounded-full">
                            {evals.length} eval.
                          </span>
                        ) : (
                          <span className="text-slate-400">Sin eval.</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {avg !== null ? (
                          <span className={`font-bold ${getScoreColor(avg)}`}>{avg.toFixed(2)}/5</span>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`whitespace-nowrap text-xs px-2.5 py-1 rounded-full border font-medium ${getBadge(dec)}`}>
                          {getBadgeLabel(dec)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full">
                          1ª Ronda
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs">
                        <span className="flex items-center gap-1.5">
                          <span className="w-3.5 h-3.5 flex items-center justify-center text-teal-600">
                            <i className="ri-mail-check-line" />
                          </span>
                          {p.email}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{p.fechaDecision}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
